import { type PromoCodeCreatePostBody, type PromoCodeList } from "@repo/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { getErrorMessage } from "lib/error.utils";
import { queryKeys } from "lib/query-keys";
import { createPromoCodes } from "services/promo-codes/promoCodes";

export interface UsePromoCodesCreateReturn {
  createPromoCodes: (formData: PromoCodeCreatePostBody) => Promise<PromoCodeList>;
  loading: boolean;
  error: string | null;
  success: boolean;
  reset: () => void;
}

export const usePromoCodesCreate = (): UsePromoCodesCreateReturn => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error, isSuccess, reset } = useMutation({
    mutationFn: createPromoCodes,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.promoCodes.all });
    },
  });

  const createPromoCodesHandler = useCallback(
    async (formData: PromoCodeCreatePostBody): Promise<PromoCodeList> => {
      return mutateAsync(formData);
    },
    [mutateAsync],
  );

  return {
    createPromoCodes: createPromoCodesHandler,
    loading: isPending,
    error: error ? getErrorMessage(error) : null,
    success: isSuccess,
    reset,
  };
};
